/* eslint-disable lines-between-class-members */

import * as THREE from "three";
import { UniformsUtils } from "three";
import { logicalChildrenOf, ScriptableMixin, ScriptableObject, ScriptableObjects } from "./ScriptableObjects.js";

/* ────────────────────────────────────────────────────────────────────────── */
/* Environment & helpers                                                      */
/* ────────────────────────────────────────────────────────────────────────── */
const HAS_AFRAME = typeof window !== "undefined" && !!window.AFRAME;
const SkyboxCtor = (typeof globalThis !== "undefined" && (globalThis.SkyboxNode || globalThis.Skybox)) || undefined;

const isAFrameEntity = v => !!(v && typeof v.addEventListener === "function" && v.object3D);
const isObject3D = v => !!(v && (v.isObject3D || (typeof THREE !== "undefined" && v instanceof THREE.Object3D)));

/* ────────────────────────────────────────────────────────────────────────── */
/* Public API: properties                                                     */
/* ────────────────────────────────────────────────────────────────────────── */
export const SKYBOX_PROPS = [
  "turbidity",
  "rayleigh",
  "mieCoefficient",
  "mieDirectionalG",
  "inclination",
  "azimuth",
  "distance"
];

/* ────────────────────────────────────────────────────────────────────────── */
/* Editor / plain-Three adapter                                               */
/* ────────────────────────────────────────────────────────────────────────── */
class SkyboxEditorAdapter {
  constructor(sourceObject, originalDescriptors = new Map()) {
    this.sourceObject = sourceObject;
    this._values = new Map();
    this._originalDescriptors = originalDescriptors;

    SKYBOX_PROPS.forEach(prop => {
      Object.defineProperty(this, prop, {
        get: () => this._values.get(prop),
        set: v => {
          this._values.set(prop, v);
          const desc = this._originalDescriptors.get(prop);
          
          if (desc?.set) {
            try {
              desc.set.call(this.sourceObject, v);
            } catch {
              /* ignore */
            }
          } else {
            try {
              this.sourceObject[prop] = v;
            } catch {
              /* ignore */
            }
          }
          
          // Trigger onChange to update the skybox environment map
          if (typeof this.sourceObject.onChange === "function") {
            this.sourceObject.onChange();
          }
        }
      });

      // seed initial value (best-effort, avoid triggering setters)
      const d = originalDescriptors.get(prop);
      let initial;
      try {
        if (d?.get) initial = d.get.call(sourceObject);
        else if (Object.prototype.hasOwnProperty.call(sourceObject, prop)) initial = sourceObject[prop];
      } catch {
        /* ignore */
      }
      this._values.set(prop, initial);
    });
  }

  dispose() {
    this._values.clear();
    this.sourceObject = null;
  }
}

/* ────────────────────────────────────────────────────────────────────────── */
/* A-Frame adapter                                                            */
/* ────────────────────────────────────────────────────────────────────────── */
class SkyboxAFrameAdapter {
  constructor(sourceObject) {
    this.sourceObject = sourceObject;

    const SKYBOX_COMPONENT = "skybox";
    const _pendingUpdates = {};
    this._pendingUpdates = _pendingUpdates;

    const _flushPending = () => {
      if (!this.sourceObject?.el) return;
      const current = {
        ...(this.sourceObject.el.getAttribute(SKYBOX_COMPONENT) || {})
      };
      Object.assign(current, _pendingUpdates);
      this.sourceObject.el.setAttribute(SKYBOX_COMPONENT, current);
      Object.keys(_pendingUpdates).forEach(k => delete _pendingUpdates[k]);
    };

    if (this.sourceObject?.el) {
      this.sourceObject.el.addEventListener("componentinitialized", e => {
        if (e?.detail?.name === SKYBOX_COMPONENT) _flushPending();
      });
    }

    // Helper setters/getters ------------------------------------------------
    const setComponentProp = (key, val) => {
      if (!this.sourceObject?.el) return;
      const attr = this.sourceObject.el.getAttribute(SKYBOX_COMPONENT);
      if (!attr) {
        _pendingUpdates[key] = val;
        return;
      }

      const current = { ...attr };
      current[key] = val;
      this.sourceObject.el.setAttribute(SKYBOX_COMPONENT, current);
    };

    const getComponentProp = key => {
      const attr = this.sourceObject?.el?.getAttribute(SKYBOX_COMPONENT);
      if (attr) {
        return attr[key];
      }
      return _pendingUpdates[key];
    };

    // skybox component props ------------------------------------------------
    SKYBOX_PROPS.forEach(prop => {
      Object.defineProperty(this, prop, {
        get: () => getComponentProp(prop),
        set: v => setComponentProp(prop, v)
      });
    });
  }

  dispose() {
    this.sourceObject = null;
  }
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Lazy adapter bootstrap (works for adopted instances)                       */
/* ────────────────────────────────────────────────────────────────────────── */
function collectDescriptorsFor(obj) {
  const map = new Map();
  for (const prop of SKYBOX_PROPS) {
    let cur = obj;
    while (cur) {
      const d = Object.getOwnPropertyDescriptor(cur, prop);
      if (d) {
        map.set(prop, d);
        break;
      }
      cur = Object.getPrototypeOf(cur);
    }
    if (!map.has(prop)) map.set(prop, undefined);
  }
  return map;
}

function ensureAdapter(self) {
  if (self._adapter) return self._adapter;

  if (HAS_AFRAME && (self.el || self.__el)) {
    Object.defineProperty(self, "_adapter", { value: new SkyboxAFrameAdapter(self), configurable: true });
    return self._adapter;
  }

  const descriptors = collectDescriptorsFor(self);
  Object.defineProperty(self, "_adapter", { value: new SkyboxEditorAdapter(self, descriptors), configurable: true });
  return self._adapter;
}

export class SkyboxElement extends ScriptableObject {
  static scriptableType = "skybox";

  constructor(initial = {}) {
    super();

    // A-Frame creation: build <a-entity>, adopt, and return the shifted object
    if (HAS_AFRAME) {
      const el = document.createElement("a-entity");
      
      el.setAttribute("skybox", {
        turbidity: initial.turbidity !== undefined ? initial.turbidity : 10,
        rayleigh: initial.rayleigh !== undefined ? initial.rayleigh : 2,
        mieCoefficient: initial.mieCoefficient !== undefined ? initial.mieCoefficient : 0.005,
        mieDirectionalG: initial.mieDirectionalG !== undefined ? initial.mieDirectionalG : 0.8,
        inclination: initial.inclination !== undefined ? initial.inclination : 0.49,
        azimuth: initial.azimuth !== undefined ? initial.azimuth : 0.25,
        distance: initial.distance !== undefined ? initial.distance : 8000
      });

      const obj = this.constructor.adopt(el);
      if (Object.getPrototypeOf(obj) !== this.constructor.prototype) {
        Object.setPrototypeOf(obj, this.constructor.prototype);
      }

      // lazy init adapter and apply initial props
      ensureAdapter(obj);
      for (const k of Object.keys(initial)) {
        if (k in obj) obj[k] = initial[k];
      }

      return obj; // IMPORTANT: return shifted object
    }

    // Editor / plain Three: construct real SkyboxNode and upgrade to scriptable
    const node = createSkyboxElementEditor(initial);
    return node;
  }

  copy(source, recursive = true) {
    if (!source || !source.isScriptableObject) return this;

    // 1) copy transform only (no children)
    THREE.Object3D.prototype.copy.call(this, source, /* recursive = */ false);

    // 2) copy public skybox props via adapter
    const take = (prop, val) => {
      if (val === undefined) return;
      
      if (Array.isArray(val)) {
        this[prop] = val.slice();
      } else if (val && typeof val.clone === "function") {
        this[prop] = val.clone();
      } else {
        this[prop] = val;
      }
    };

    for (const p of SKYBOX_PROPS) {
      take(p, source[p]);
    }

    // 3) clear existing logical children
    if (this.children?.length) {
      for (const c of [...this.children]) THREE.Object3D.prototype.remove.call(this, c);
    }

    // 4) replicate children via our public API
    if (recursive) {
      for (const srcChild of logicalChildrenOf(source)) {
        this.add(srcChild.clone());
      }
    }

    return this;
  }

  clone(recursive = true) {
    const init = {};

    for (const p of SKYBOX_PROPS) {
      const v = this[p];
      if (v === undefined) continue;
      
      if (Array.isArray(v)) {
        init[p] = v.slice();
      } else if (v && typeof v.clone === "function") {
        init[p] = v.clone();
      } else {
        init[p] = v;
      }
    }

    const dest = new SkyboxElement(init);

    // Copy transforms/children; copy() also uses the adapter surface.
    dest.position.copy(this.position);
    dest.quaternion.copy(this.quaternion);
    dest.scale.copy(this.scale);

    return dest.copy(this, recursive);
  }

  // convenience (optional)
  static create(initial = {}) {
    return new this(initial);
  }
}

// DRY helper: upgrade a plain Editor SkyboxNode instance to Scriptable, attach adapter and proxies
function makeEditorSkyboxNodeScriptable(node) {
  if (!node) return node;
  if (node.isScriptableObject === true) return node;

  // Preserve original chain and add Scriptable API on top
  const originalProto = Object.getPrototypeOf(node);
  const ScriptableClass = ScriptableMixin((originalProto && originalProto.constructor) || SkyboxCtor || THREE.Object3D);
  if (Object.getPrototypeOf(ScriptableClass.prototype) !== originalProto) {
    Object.setPrototypeOf(ScriptableClass.prototype, originalProto);
  }
  Object.setPrototypeOf(node, ScriptableClass.prototype);

  // Annotate type
  try {
    if (!node.scriptableType) Object.defineProperty(node, "scriptableType", { value: SkyboxElement.scriptableType, configurable: true });
  } catch (_) {
    node.scriptableType = SkyboxElement.scriptableType;
  }

  // Attach adapter and per-instance property proxies
  const descriptors = collectDescriptorsFor(node);
  try {
    Object.defineProperty(node, "_adapter", { value: new SkyboxEditorAdapter(node, descriptors), configurable: true });
  } catch (_) {
    node._adapter = new SkyboxEditorAdapter(node, descriptors);
  }

  for (const prop of SKYBOX_PROPS) {
    try {
      Object.defineProperty(node, prop, {
        get() {
          return this._adapter?.[prop];
        },
        set(v) {
          if (this._adapter) this._adapter[prop] = v;
        },
        enumerable: true,
        configurable: true
      });
    } catch (_) {
      /* ignore define errors */
    }
  }

  // Ensure SkyboxElement's enhanced `clone` logic is used for
  // editor-created skybox nodes.
  try {
    if (node.clone !== SkyboxElement.prototype.clone) {
      Object.defineProperty(node, "clone", {
        value: function(recursive = true) {
          const cloned = SkyboxElement.prototype.clone.call(this, recursive);
          
          // Handle special skybox material cloning
          if (typeof cloned.onChange === "function") {
            cloned.onChange();
          }
          if (cloned.sky && cloned.sky.material) {
            const clonedMaterial = cloned.sky.material.clone();
            if (clonedMaterial.uniforms) {
              clonedMaterial.uniforms = UniformsUtils.clone(cloned.sky.material.uniforms);
            }
            cloned.sky.material = clonedMaterial;
          }
          return cloned;
        },
        writable: true,
        configurable: true
      });
    }
  } catch {
    node.clone = SkyboxElement.prototype.clone;
  }

  return node;
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Bind property surface (works for adopted instances too)                    */
/* ────────────────────────────────────────────────────────────────────────── */
SKYBOX_PROPS.forEach(prop => {
  Object.defineProperty(SkyboxElement.prototype, prop, {
    get() {
      return ensureAdapter(this)?.[prop];
    },
    set(v) {
      const a = ensureAdapter(this);
      if (a) a[prop] = v;
    },
    enumerable: true,
    configurable: true
  });
});

/* ────────────────────────────────────────────────────────────────────────── */
/* Optional editor factory helper                                             */
/* ────────────────────────────────────────────────────────────────────────── */
export function createSkyboxElementEditor(initial = {}) {
  const SkyboxCtor = (typeof globalThis !== "undefined" && (globalThis.SkyboxNode || globalThis.Skybox)) || undefined;
  
  if (typeof SkyboxCtor !== "function") {
    throw new Error("SkyboxElementEditor: SkyboxNode constructor not found.");
  }

  let node;
  try {
    const editor = ScriptableObject.editor || globalThis.editor;
    node = new SkyboxCtor(editor);
  } catch (e) {
    throw new Error(`SkyboxElementEditor: failed to construct – ${e?.message || e}`);
  }

  makeEditorSkyboxNodeScriptable(node);

  for (const k of Object.keys(initial)) {
    if (k in node) node[k] = initial[k];
  }

  if (typeof node.onChange === "function") {
    node.onChange();
  }
  
  return node;
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Registry: promote A-Frame elements and pure-Three objects                  */
/* ────────────────────────────────────────────────────────────────────────── */
ScriptableObjects.registerType("skybox", source => {
  if (!source) return source;

  const type = source.scriptableType || source.object3D?.scriptableType || source?.__el?.object3D?.scriptableType || source?.el?.object3D?.scriptableType;

  if (type !== SkyboxElement.scriptableType) return source;

  // A-Frame entities: use base adopt via class method
  if (isAFrameEntity(source)) return SkyboxElement.adopt(source);

  // Editor/plain-Three nodes: upgrade in-place via shared helper
  if (isObject3D(source)) return makeEditorSkyboxNodeScriptable(source);

  return source;
});

/* Named exports expected by external consumers */
export { SkyboxAFrameAdapter, SkyboxEditorAdapter };

/* Optional: nicer DevTools label */
Object.defineProperty(SkyboxElement.prototype, Symbol.toStringTag, { value: "SkyboxElement" });
